import { useEffect, useState, useCallback } from "react";
import PageContainer from "../../components/container/PageContainer";
import {
  TextField,
  Button,
  Stack,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import Breadcrumb from "../../layouts/full-layout/breadcrumb/Breadcrumb";
import LineNameLabel from "../../components/common/LineNameLabel";
import { useDispatch, useSelector } from "react-redux";
import {
  contactClean,
  setLoadingContacts,
  getContactsByCategories,
} from "../../actions/contacts/contacts";
import {
  getListContactsAdvanced,
  getListContactsByNameAdvanced,
  getAgentsForFilter,
  setAdvancedFilters,
} from "../../actions/contacts/contactsAdvanced";
import { useToken } from "../../hooks/useToken";
import { useMsal } from "@azure/msal-react";
import { useForm } from "../../hooks/useForm";
import TableContactsBody from "./TableBody";
import CreateContactModal from "../../components/CreateContactModal";
import ContactsFilterCategories from "./ContactsFilterCategories";

import SearchIcon from "@mui/icons-material/Search";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { useMediaQuery, useTheme } from "@mui/material";
import { fetchCategories } from "../../actions/categories";

const BCrumb = [
  {
    to: "/",
    title: "Inicio",
  },
  {
    title: "Contactos",
  },
];

const getCurrentDateTimeLocal = () => {
  const now = new Date();
  const tzOffsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 16);
};

const TableHeadContacts = () => {
  const { id_customer } = useSelector((state) => state.agent);
  const { contacts, count, loading } = useSelector((state) => state.contact);
  const categories = useSelector((state) => state.categories.items || {});
  const contactsAdvanced = useSelector(
    (state) =>
      state.contactsAdvanced || {
        contacts: [],
        loading: false,
        count: 0,
        agents: [],
        filters: {},
      },
  );

  const { instance } = useMsal();
  const { tokenRequest } = useToken();
  const dispatch = useDispatch();
  const [formValues, handleInputChange] = useForm({ searcher: "" });
  const { searcher } = formValues;
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCreateOpen, setModalCreateOpen] = useState(false);
  const [listOfCategories, setListOfCategories] = useState([]);

  // ⭐ AGREGAR: Estados para la paginación de categorías
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 5; // Número de categorías por página

  const [quickFilters, setQuickFilters] = useState({
    agent_filter_type: "all",
    id_agent: null,
    date_from: null,
    date_to: null,
  });
  
  const [hasInitialized, setHasInitialized] = useState(false);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const fetchCategoriesData = useCallback(
    (page = 0) => {
      const offset = page * ITEMS_PER_PAGE;
      instance.acquireTokenSilent(tokenRequest).then(({ accessToken }) => {
        dispatch(
          fetchCategories(id_customer, accessToken, offset, ITEMS_PER_PAGE),
        );
      });
    },
    [instance, tokenRequest, dispatch, id_customer],
  );

  // Cargar categorías al abrir el modal
  useEffect(() => {
    if (modalOpen) {
      fetchCategoriesData(0);
      setCurrentPage(0);
    }
  }, [modalOpen, fetchCategoriesData]);

  const handleFilter = (selectedCategories) => {
    console.debug(
      "[ContactsAdvanced/UI] Categorías seleccionadas →",
      selectedCategories,
    );

    setListOfCategories(selectedCategories);

    if (!selectedCategories || selectedCategories.length === 0) {
      handleGetContacts(id_customer);
      return;
    }

    const categoryIds = selectedCategories.map((cat) => cat.name);
    console.debug(
      "[ContactsAdvanced/UI] IDs de categorías a enviar →",
      categoryIds,
    );

    setHasInitialized(true);
    dispatch(contactClean());

    dispatch(setLoadingContacts(true));
    instance
      .acquireTokenSilent(tokenRequest)
      .then(({ accessToken }) => {
        dispatch(
          getContactsByCategories(categoryIds, id_customer, accessToken),
        );
      })
      .catch((error) => {
        console.error("[ContactsAdvanced/UI] Error al obtener token:", error);
        dispatch(setLoadingContacts(false));
      });
  };
  const handleSearch = () => {
    if (searcher.trim() !== "") {
      setHasInitialized(true);
      dispatch(setLoadingContacts(true));
      // Limpiamos el estado ANTES de la nueva búsqueda para evitar duplicados
      dispatch(contactClean());
      instance
        .acquireTokenSilent(tokenRequest)
        .then(({ accessToken }) =>
          dispatch(
            getListContactsByNameAdvanced(searcher, id_customer, accessToken),
          ),
        );
    } else {
      handleGetContacts(id_customer);
    }
  };

  const handleGetContacts = (id_customer) => {
    setHasInitialized(true);
    dispatch(setLoadingContacts(true));
    handleInputChange({ target: { name: "searcher", value: "" } });
    dispatch(contactClean());
    dispatch(contactClean());

    instance
      .acquireTokenSilent(tokenRequest)
      .then(({ accessToken }) =>
        dispatch(getListContactsAdvanced(id_customer, accessToken, {}, 0, 5)),
      );
  };

  const handleApplyFilters = (filters) => {
    // console.debug('[ContactsAdvanced/UI] handleApplyFilters → incoming filters:', filters);
    // Marcamos como inicializado para evitar que el useEffect dispare una carga "base" en paralelo
    setHasInitialized(true);
    dispatch(setLoadingContacts(true));
    instance.acquireTokenSilent(tokenRequest).then(({ accessToken }) => {
      const formattedFilters = {
        ...filters,
        date_from: filters.date_from || null,
        date_to: filters.date_to || null,
      };
      // console.debug('[ContactsAdvanced/UI] formattedFilters →', formattedFilters);
      dispatch(
        getListContactsAdvanced(id_customer, accessToken, formattedFilters, 0, 5),
      );
    });
  };

  const handleQuickFilterChange = (field, value) => {
    const newFilters = {
      ...quickFilters,
      [field]: value,
    };
    // console.debug('[ContactsAdvanced/UI] handleQuickFilterChange →', field, value, newFilters);
    setQuickFilters(newFilters);
    dispatch(setAdvancedFilters(newFilters));
  };

  const handleApplyQuickFilters = () => {
    handleApplyFilters(quickFilters);
  };

  useEffect(() => {
    if (!isDesktop || hasInitialized) return;

    // Evitar carga "base" si hay filtros activos (para que no sobreescriba la búsqueda filtrada)
    const f = contactsAdvanced?.filters || {};
    const hasAnyFilter = Boolean(
      (f.id_agent && f.id_agent !== "") ||
      (f.agent_filter_type && f.agent_filter_type !== "all") ||
      f.date_from ||
      f.date_to,
    );

    if (hasAnyFilter) {
      // console.debug('[ContactsAdvanced/UI] useEffect: filtros activos detectados, se omite carga base');
      return;
    }

    if (!contactsAdvanced.loading) {
      // console.debug('[ContactsAdvanced/UI] useEffect: disparando carga base sin filtros');
      setHasInitialized(true);
      dispatch(setLoadingContacts(true));
      instance.acquireTokenSilent(tokenRequest).then(({ accessToken }) => {
        dispatch(getListContactsAdvanced(id_customer, accessToken, {}, 0, 5));
        dispatch(getAgentsForFilter(id_customer, accessToken));
      });
    }
  }, [
    contactsAdvanced.contacts.length,
    contactsAdvanced.loading,
    contactsAdvanced.filters,
    dispatch,
    id_customer,
    instance,
    tokenRequest,
    isDesktop,
    hasInitialized,
  ]);

  // Usar contactsAdvanced si se ha inicializado (incluso si está vacío)
  const useAdvanced = contactsAdvanced.contacts !== undefined && hasInitialized;
  const currentContacts = useAdvanced ? contactsAdvanced.contacts : contacts;
  const currentCount = useAdvanced ? contactsAdvanced.count : count;
  const currentLoading = contactsAdvanced.loading || loading;
  const currentDateTimeLocal = getCurrentDateTimeLocal();

  return (
    <PageContainer title="Contactos" description="Lista de contactos">
      <Breadcrumb title={<LineNameLabel/>} items={BCrumb} />
      {/* Primera fila: Búsqueda y botones principales */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          marginBottom: 2,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <TextField
            id="outlined-basic"
            label="Buscar contacto"
            variant="outlined"
            name="searcher"
            value={searcher}
            onChange={handleInputChange}
            onKeyPress={(ev) => {
              if (ev.key === "Enter") {
                handleSearch();
                ev.preventDefault();
              }
            }}
            sx={{ flex: 1, minWidth: 150, height: "56px" }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            sx={{ height: "56px", px: 3 }}
          >
            Buscar
          </Button>
          <Button
            variant="contained"
            onClick={() => handleGetContacts(id_customer)}
            startIcon={<RestoreFromTrashIcon />}
            sx={{ height: "56px", px: 3 }}
          >
            Limpiar Consulta
          </Button>
          <Button
            variant="contained"
            onClick={() => setModalOpen(true)}
            startIcon={<FilterAltIcon />}
            sx={{ height: "56px", px: 3 }}
          >
            Categorías
          </Button>
        </Stack>

        <Button
          variant="contained"
          onClick={() => setModalCreateOpen(true)}
          startIcon={<AddIcon />}
          sx={{ height: "56px", px: 3 }}
        >
          Agregar Contacto
        </Button>

        <ContactsFilterCategories
          open={modalOpen}
          categories={categories}
          handleClose={() => setModalOpen(false)}
          handleFilter={handleFilter}
          fetchCategoriesData={fetchCategoriesData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={ITEMS_PER_PAGE}
          initialSelectedCategories={listOfCategories} // ⭐ Pasar categorías activas
        />
      </Stack>

      {/* Segunda fila: Filtros rápidos integrados */}
      <Box
        sx={{
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          mb: 2,
          backgroundColor: "#f8f9fa",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de Asignación</InputLabel>
              <Select
                value={quickFilters.agent_filter_type}
                label="Tipo de Asignación"
                onChange={(e) =>
                  handleQuickFilterChange("agent_filter_type", e.target.value)
                }
              >
                <MenuItem value="all">Todos los contactos</MenuItem>
                <MenuItem value="assigned">Solo asignados</MenuItem>
                <MenuItem value="unassigned">Sin asignar</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Agente Específico</InputLabel>
              <Select
                value={quickFilters.id_agent || ""}
                label="Agente Específico"
                onChange={(e) =>
                  handleQuickFilterChange("id_agent", e.target.value || null)
                }
              >
                <MenuItem value="">Ninguno</MenuItem>
                {contactsAdvanced.agents &&
                  contactsAdvanced.agents.map((agent) => (
                    <MenuItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              label="Fecha Desde"
              type="datetime-local"
              size="small"
              value={quickFilters.date_from || ""}
              onChange={(e) =>
                handleQuickFilterChange("date_from", e.target.value)
              }
              inputProps={{
                max: quickFilters.date_to || currentDateTimeLocal,
              }}
              InputLabelProps={{ shrink: true }}
              onKeyDown={(e) => e.preventDefault()}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              label="Fecha Hasta"
              type="datetime-local"
              size="small"
              value={quickFilters.date_to || ""}
              onChange={(e) =>
                handleQuickFilterChange("date_to", e.target.value)
              }
              inputProps={{
                max: currentDateTimeLocal,
                min: quickFilters.date_from || undefined,
              }}
              InputLabelProps={{ shrink: true }}
              onKeyDown={(e) => e.preventDefault()}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={handleApplyQuickFilters}
                disabled={currentLoading}
                startIcon={<FilterAltIcon />}
                sx={{ flex: 1 }}
              >
                Filtrar
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setQuickFilters({
                    agent_filter_type: "all",
                    id_agent: null,
                    date_from: null,
                    date_to: null,
                  });
                  handleGetContacts(id_customer);
                }}
                disabled={currentLoading}
                startIcon={<ClearIcon />}
                sx={{ flex: 1 }}
              >
                Limpiar
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <TableContactsBody
        contacts={currentContacts}
        loading={currentLoading}
        count={currentCount}
        id_customer={id_customer}
        agents={contactsAdvanced.agents || []}
        appliedFilters={quickFilters}
      />
      <CreateContactModal
        open={modalCreateOpen}
        onClose={() => setModalCreateOpen(false)}
      />
    </PageContainer>
  );
};

export default TableHeadContacts;
